import {useEffect, useMemo, useRef, useState} from 'react';
import {SelectOption} from '@legit.health/ui';
import {useTranslation} from 'react-i18next';
import sentry from '@/shared/utils/sentry';

type Args = {
  enableAudioSource: boolean;
};

export default function useWebcam({enableAudioSource}: Args) {
  const [audioSourceOptions, setAudioSourceOptions] = useState<SelectOption[] | null>(null);
  const [videoSourceOptions, setVideoSourceOptions] = useState<SelectOption[] | null>(null);
  const [audioSource, setAudioSource] = useState<string | null>(null);
  const [videoSource, setVideoSource] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const {t} = useTranslation(['ui']);

  useEffect(
    function () {
      const videoElement = videoRef.current;
      async function prepareStream() {
        function gotDevices(deviceInfos: MediaDeviceInfo[]) {
          const audioSourceOptions = [];
          const videoSourceOptions = [];
          for (const deviceInfo of deviceInfos) {
            if (deviceInfo.kind === 'audioinput') {
              audioSourceOptions.push({
                value: deviceInfo.deviceId,
                label: deviceInfo.label || `Microphone ${deviceInfo.deviceId}`
              });
            } else if (deviceInfo.kind === 'videoinput') {
              videoSourceOptions.push({
                value: deviceInfo.deviceId,
                label: deviceInfo.label || `Camera ${deviceInfo.deviceId}`
              });
            }
          }
          setAudioSourceOptions(audioSourceOptions);
          setVideoSourceOptions(videoSourceOptions);
        }

        function gotStream(stream: MediaStream) {
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        }

        function getDevices() {
          return navigator.mediaDevices.enumerateDevices();
        }

        async function getStream() {
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
          }

          const constraints: MediaStreamConstraints = {
            video: {
              deviceId: videoSource ? {exact: videoSource} : undefined
            }
          };
          if (enableAudioSource) {
            constraints.audio = {deviceId: audioSource ? {exact: audioSource} : undefined};
          }

          try {
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            gotStream(stream);
          } catch (error) {
            sentry.captureException(error);
            setError(new Error(t('ui:form.errors.webcam.errorGettingMedias')));
          }
        }

        await getStream();
        const mediaDevices = await getDevices();
        gotDevices(mediaDevices);
      }

      prepareStream();
      return function cleanUp() {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
        }
        if (videoElement) {
          videoElement.srcObject = null;
        }
      };
    },
    [audioSource, enableAudioSource, t, videoSource]
  );
  const webcam = useMemo(
    () => ({
      audioSourceOptions,
      videoSourceOptions,
      audioSource,
      setAudioSource,
      videoSource,
      setVideoSource,
      error,
      streamRef,
      videoRef
    }),
    [audioSource, audioSourceOptions, error, videoSource, videoSourceOptions]
  );
  return webcam;
}
